import React from "react";
import { Helmet } from "react-helmet-async";
import { dict } from "../i18n";
import { PAGE_KEYS, PAGE_SLUGS, pagePath } from "../lib/routes";
import { PRICING_SECTIONS } from "../lib/pricingSections";
import { DISEASE_SLUGS } from "../data/diseaseSlugs";
import { FAQ_ITEMS } from "../data/faq";
import { SITE, PAGE_TITLES, PAGE_DESCRIPTIONS } from "../data/seoMeta";
import {
  buildClinicSchema,
  buildWebSiteSchema,
  buildFaqSchema,
  buildBreadcrumbSchema,
  buildArticleSchema,
  buildOfferCatalogSchema,
  buildPhysiciansSchema,
} from "../lib/schema";

// Lookup: ET pricing sub-slug (canonical id) → all-language slugs
const PRICING_SUBSLUGS_BY_ET = Object.fromEntries(
  PRICING_SECTIONS.map((s) => [s.slugs.et, s.slugs]),
);
// Lookup: ET disease article slug → all-language slugs
const DISEASE_SUBSLUGS_BY_ET = Object.fromEntries(
  DISEASE_SLUGS.map((d) => [d.slugs.et, d.slugs]),
);

/**
 * Translate a sub-slug (2nd URL segment) for a given parent page key.
 * Returns undefined if the parent page has no translated sub-slugs.
 */
function translateSubSlug(pageKey, etSubSlug, targetLang) {
  if (pageKey === "pricing") return PRICING_SUBSLUGS_BY_ET[etSubSlug]?.[targetLang] || etSubSlug;
  if (pageKey === "diseases") return DISEASE_SUBSLUGS_BY_ET[etSubSlug]?.[targetLang] || etSubSlug;
  return etSubSlug;
}

/**
 * Build the language-specific canonical + hreflang set for a given page key
 * (or arbitrary subpath, e.g. `/hinnakiri/diabeedi-uuringud`). Uses translated
 * slugs from PAGE_SLUGS + pricingSections + diseaseSlugs so /ru/цены/исследования-диабета,
 * /en/pricing/diabetes-tests etc. are all correct.
 */
function buildLangUrls(page, pathOverride) {
  // When pathOverride is provided (article pages, pricing sections), we treat
  // it as a full ET path starting with "/". We split off the "page slug" (first
  // segment) and translate it, keeping the remainder untouched.
  if (pathOverride) {
    const safe = pathOverride.startsWith("/") ? pathOverride : `/${pathOverride}`;
    const match = safe.match(/^\/([^/]+)(?:\/([^/]+))?(\/.*)?$/);
    const firstSeg = match ? match[1] : "";
    const secondSeg = match && match[2] ? match[2] : "";
    const tail = match && match[3] ? match[3] : "";

    // Find which page this ET slug represents
    let pageKey = null;
    for (const key of PAGE_KEYS) {
      if (PAGE_SLUGS[key]?.et === firstSeg) {
        pageKey = key;
        break;
      }
    }
    if (!pageKey) {
      // Unknown override — same URL across all langs (with language prefix)
      return {
        et: `${SITE}${safe}`,
        ru: `${SITE}/ru${safe}`,
        en: `${SITE}/en${safe}`,
      };
    }

    const buildForLang = (lang) => {
      const base = pagePath(pageKey, lang);
      if (!secondSeg) return `${SITE}${base}${tail}`;
      const localizedSub = translateSubSlug(pageKey, secondSeg, lang);
      return `${SITE}${base}/${localizedSub}${tail}`;
    };
    return { et: buildForLang("et"), ru: buildForLang("ru"), en: buildForLang("en") };
  }

  const pageKey = page || "home";
  return {
    et: `${SITE}${pagePath(pageKey, "et") || "/"}`,
    ru: `${SITE}${pagePath(pageKey, "ru")}`,
    en: `${SITE}${pagePath(pageKey, "en")}`,
  };
}

/**
 * @param articleMeta  { title, summary, image, updated } — emits MedicalWebPage
 * @param breadcrumb   [{ name, path }] — trail after the home crumb
 * @param offers       { name, groups } — emits OfferCatalog (pricing pages)
 * @param doctors      clinic.doctors array — emits Physician ItemList
 */
export default function SEO({
  lang,
  page = "home",
  title,
  description,
  pathOverride,
  articleMeta,
  breadcrumb,
  offers,
  doctors,
}) {
  const t = dict[lang];

  // URL resolution — pathOverride wins (used by article pages).
  // Otherwise use the registered route for this page key.
  const urls = buildLangUrls(page, pathOverride);
  const canonical = urls[lang] || urls.et;
  const homeUrl = `${SITE}${pagePath("home", lang) || "/"}`;

  const finalTitle = title || PAGE_TITLES[page]?.[lang] || PAGE_TITLES.home[lang];
  const finalDesc = description || PAGE_DESCRIPTIONS[page]?.[lang] || PAGE_DESCRIPTIONS.home[lang];

  const schemas = [buildClinicSchema(lang), buildWebSiteSchema(lang, homeUrl)];

  // FAQPage is emitted ONLY on the Contact page, where the same questions are
  // visible to users (Google requires markup to mirror visible content, and
  // answer engines quote visible text).
  if (page === "contact") schemas.push(buildFaqSchema(FAQ_ITEMS, lang));

  if (breadcrumb && breadcrumb.length > 0) {
    schemas.push(
      buildBreadcrumbSchema(
        breadcrumb.map((c) => ({ name: c.name, url: `${SITE}${c.path}` })),
        lang,
        homeUrl,
      ),
    );
  }

  if (articleMeta) schemas.push(buildArticleSchema({ lang, canonical, article: articleMeta }));

  if (offers) {
    const catalog = buildOfferCatalogSchema({
      lang,
      canonical,
      name: offers.name || finalTitle,
      groups: offers.groups,
    });
    if (catalog) schemas.push(catalog);
  }

  if (doctors) {
    const team = buildPhysiciansSchema({ lang, canonical, doctors });
    if (team) schemas.push(team);
  }

  return (
    <Helmet>
      <html lang={lang} />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="et" href={urls.et} />
      <link rel="alternate" hrefLang="ru" href={urls.ru} />
      <link rel="alternate" hrefLang="en" href={urls.en} />
      <link rel="alternate" hrefLang="x-default" href={urls.et} />

      {/* Open Graph */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:type" content={articleMeta ? "article" : "website"} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={t.locale} />
      <meta property="og:site_name" content="Tallinna Endokrinoloogia Kliinik" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />

      {/* AEO / GEO — AI engine signals */}
      <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />
      <meta name="ai-content-declaration" content="human-curated" />
      <meta name="geo.region" content="EE-37" />
      <meta name="geo.placename" content="Tallinn, Pärnu" />
      <meta name="geo.position" content="59.4370;24.7536" />
      <meta name="ICBM" content="59.4370, 24.7536" />

      {/* Structured data */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
